/**
 * L'état d'interface de « Libérer des créneaux » : le brouillon (les créneaux marqués, la semaine
 * retouchée, le motif), l'étape et la boîte. Pur, testé.
 *
 * Port de `ReleaseSlotsScreen.js` : toucher une heure avec un live la marque (barrée), un second
 * toucher la rend ; une heure libre se propose ou se retire, comme dans « Ma dispo » ; une heure
 * libérée par un geste précédent est inerte. Marquer une heure remet le motif à « changement
 * d'emploi du temps » : « J'arrête tous mes créneaux » est le seul geste qui dise « j'arrête ».
 */
import { RELEASE_MOTIF, type ReleaseMotif } from "../../../../adapters/tutor/Availability/tutorReleaseAdapter"
import { slotKeyOf, withoutSlot, withSlot } from "../../../../adapters/tutor/shared/tutorWeekAdapter"
import { type UITutorHour, type UITutorReleaseStep } from "../../../types/tutorDispoOffres"

export type TutorReleaseSlotsUiState = {
    step: UITutorReleaseStep
    selectedKeys: string[]
    preselectedKey: string | null
    draftOpenSlots: string[] | null
    motif: ReleaseMotif
    previewAsked: boolean
    /** « Tu risques de perdre… », avant « Tes élèves ». */
    lossCount: number | null
    errorMessage: string | null
}

export type TutorReleaseSlotsUiAction =
    | { type: "PRESS_HOUR"; hour: UITutorHour; serverOpenSlots: string[] }
    /** Le live d'où part le geste est retrouvé sur la semaine : son créneau est marqué. */
    | { type: "PRESELECT"; key: string }
    | { type: "SET_DRAFT"; draft: string[] }
    | { type: "ASK_PREVIEW"; motif: ReleaseMotif }
    | { type: "WARN_LOSS"; count: number }
    | { type: "CLOSE_LOSS" }
    | { type: "GO_STEP"; step: UITutorReleaseStep }
    | { type: "SHOW_ERROR"; message: string }
    | { type: "DISMISS_ERROR" }

/** L'état d'ouverture : un créneau passé par la route (une heure de « Ma dispo ») est déjà marqué. */
export function initialTutorReleaseSlotsUiState(slot: string | null | undefined): TutorReleaseSlotsUiState {
    const key = slot ? slotKeyOf(slot) : null
    return {
        step: "week",
        selectedKeys: key ? [key] : [],
        preselectedKey: key,
        draftOpenSlots: null,
        motif: RELEASE_MOTIF.SCHEDULE_CHANGE,
        previewAsked: false,
        lossCount: null,
        errorMessage: null,
    }
}

export function tutorReleaseSlotsUiReducer(
    state: TutorReleaseSlotsUiState,
    action: TutorReleaseSlotsUiAction
): TutorReleaseSlotsUiState {
    switch (action.type) {
        case "PRESS_HOUR": {
            const { hour, serverOpenSlots } = action
            if (hour.state === "live" || (hour.state === "freed" && hour.releasable)) {
                const selectedKeys = state.selectedKeys.includes(hour.id)
                    ? state.selectedKeys.filter((key) => key !== hour.id)
                    : [...state.selectedKeys, hour.id]
                return { ...state, selectedKeys, motif: RELEASE_MOTIF.SCHEDULE_CHANGE }
            }
            if (hour.state === "freed") return state
            const base = state.draftOpenSlots ?? serverOpenSlots
            const draftOpenSlots = hour.state === "taken" ? withoutSlot(base, hour.id) : withSlot(base, hour.id)
            return { ...state, draftOpenSlots }
        }
        case "PRESELECT":
            return { ...state, selectedKeys: [action.key], preselectedKey: action.key }
        case "SET_DRAFT":
            return { ...state, draftOpenSlots: action.draft }
        case "ASK_PREVIEW":
            return { ...state, motif: action.motif, previewAsked: true }
        case "WARN_LOSS":
            return { ...state, lossCount: action.count }
        case "CLOSE_LOSS":
            return { ...state, lossCount: null }
        case "GO_STEP":
            return { ...state, step: action.step, lossCount: null }
        case "SHOW_ERROR":
            return { ...state, errorMessage: action.message }
        case "DISMISS_ERROR":
            return { ...state, errorMessage: null }
        default:
            return state
    }
}
